import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DAYS_OF_WEEK, DaySpecificTimes } from '@/types/shift-templates';

interface DaySpecificHoursProps {
  value: DaySpecificTimes;
  onChange: (value: DaySpecificTimes) => void;
  defaultStart: string;
  defaultEnd: string;
}

export function DaySpecificHours({
  value,
  onChange,
  defaultStart,
  defaultEnd
}: DaySpecificHoursProps) {
  const { t } = useTranslation();

  const handleDayToggle = (dayValue: number, checked: boolean) => {
    const dayKey = String(dayValue);
    const newValue = { ...value };

    if (checked) {
      // Add day with default times
      newValue[dayKey] = { start: defaultStart, end: defaultEnd };
    } else {
      // Remove day
      delete newValue[dayKey];
    }

    onChange(newValue);
  };

  const handleTimeChange = (
    dayValue: number,
    field: 'start' | 'end',
    time: string
  ) => {
    const dayKey = String(dayValue);
    const current = value[dayKey] || { start: defaultStart, end: defaultEnd };

    onChange({
      ...value,
      [dayKey]: {
        ...current,
        [field]: time,
      },
    });
  };

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <p className="text-sm text-muted-foreground mb-3">
        {t('shiftTemplates.selectSpecificDays')}
      </p>

      {DAYS_OF_WEEK.map((day) => {
        const dayKey = String(day.value);
        const isEnabled = dayKey in value;
        const dayTimes = value[dayKey];

        return (
          <div key={day.value} className="flex items-center gap-3">
            <Checkbox
              id={`day-${day.value}`}
              checked={isEnabled}
              onCheckedChange={(checked) =>
                handleDayToggle(day.value, checked as boolean)
              }
            />
            <Label
              htmlFor={`day-${day.value}`}
              className="w-20 text-sm font-medium"
            >
              {day.label}
            </Label>

            {isEnabled && dayTimes && (
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={dayTimes.start}
                  onChange={(e) =>
                    handleTimeChange(day.value, 'start', e.target.value)
                  }
                  className="w-28"
                />
                <span className="text-muted-foreground">{t('shiftTemplates.until')}</span>
                <Input
                  type="time"
                  value={dayTimes.end}
                  onChange={(e) =>
                    handleTimeChange(day.value, 'end', e.target.value)
                  }
                  className="w-28"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
